import React from "react";
import Link from "next/link";
import LoadingSpinner from "./LoadingSpinner";

/**
 * Button variants matching GFS design system
 */
type ButtonVariant =
  | "primary"
  | "outline"
  | "outline-accent"
  | "outline-accent-inverse"
  | "ghost"
  | "accent";

/**
 * Button sizes
 */
type ButtonSize = "default" | "full" | "compact";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant style */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Children content */
  children: React.ReactNode;
  /** Render as an <a> tag with this href */
  href?: string;
  /** Anchor target (only used with href) */
  target?: string;
  /** Anchor rel (only used with href) */
  rel?: string;
  /** Click handler (works for both button and anchor) */
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  /** Shows a spinner and "Loading" text; disables the button */
  loading?: boolean;
}

/**
 * @component Button
 * @description Atomic button component following GFS design system.
 * Uses `.text-label` typography (Arquitecta Bold 16px, uppercase, tracking-wider).
 *
 * Variants:
 * - primary: Charcoal background, white text
 * - outline: Transparent background, charcoal border, charcoal text
 * - outline-accent: Transparent background, accent-green border, white text (hover: accent-green bg)
 * - outline-accent-inverse: Transparent background, accent-green border, white text (hover: near-black bg)
 * - accent: Accent-green background, charcoal text, border (hover: white bg)
 * - ghost: No border, text only
 *
 * @example
 * <Button variant="primary">Click me</Button>
 * <Button variant="outline" size="full">Full width</Button>
 */
export default function Button({
  variant = "primary",
  size = "default",
  children,
  className = "",
  href,
  target,
  rel,
  onClick,
  loading,
  disabled,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  // Base styles: typography, spacing, accessibility
  const baseStyles =
    "text-label transition-colors inline-flex items-center justify-center gap-2";

  // Variant styles
  const variantStyles = {
    primary:
      "bg-charcoal text-white border border-transparent hover:bg-near-black",
    outline:
      "bg-transparent border-[1.5px] border-charcoal text-charcoal hover:bg-hover",
    "outline-accent":
      "bg-transparent border-[1.5px] border-accent-green text-white hover:bg-accent-green hover:text-charcoal",
    "outline-accent-inverse":
      "bg-transparent border-[1.5px] border-accent-green text-white hover:bg-near-black",
    accent:
      "bg-accent-green text-charcoal border-2 border-accent-green hover:bg-white hover:border-accent-green antialiased",
    ghost: "bg-transparent border-none text-charcoal hover:bg-hover",
  };

  // Size styles
  const sizeStyles = {
    default: "min-h-[44px] py-3 px-6",
    full: "w-full min-h-[44px] py-3 px-6",
    compact: "min-h-[38px] py-2 px-2",
  };

  const spinnerColor =
    variant === "outline" || variant === "ghost" || variant === "accent" ? "default" : "white";

  const disabledStyles = isDisabled ? "opacity-40 cursor-not-allowed pointer-events-none" : "";

  const classes =
    `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${loading ? "cursor-wait" : ""} ${disabledStyles} ${className}`.trim();

  const content = loading ? (
    <>
      <LoadingSpinner size="sm" color={spinnerColor} />
      Loading
    </>
  ) : (
    children
  );

  if (href) {
    return (
      <Link
        href={isDisabled ? "" : href}
        target={target}
        rel={rel}
        aria-disabled={isDisabled || undefined}
        className={classes}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      className={classes}
      onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
      disabled={isDisabled}
      {...props}
    >
      {content}
    </button>
  );
}
